import { Link } from 'react-router-dom'

const MembershipApplicationHero = () => {
  const membershipTiers = [
    {
      name: "Prestige",
      price: "$2,500/year",
      highlight: "Most Popular",
      benefits: ["Personal Styling", "Priority Access", "Exclusive Events"]
    },
    {
      name: "Elite",
      price: "$5,000/year", 
      highlight: "Premium",
      benefits: ["Dedicated Stylist", "Bespoke Services", "Private Appointments"]
    },
    {
      name: "Signature",
      price: "$10,000/year",
      highlight: "Ultimate",
      benefits: ["24/7 Concierge", "Global Access", "Custom Experiences"]
    }
  ]

  return (
    <section className="relative min-h-screen flex items-center bg-gradient-to-br from-luxury-black-950 to-luxury-black-900 text-white overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 right-20 w-40 h-40 border border-luxury-gold-500/20 rotate-45 hidden lg:block" />
        <div className="absolute bottom-1/4 left-20 w-32 h-32 bg-luxury-gold-500/10 rotate-45 hidden lg:block" />
        <div className="absolute top-0 left-1/3 w-px h-full bg-gradient-to-b from-transparent via-luxury-gold-500/20 to-transparent" />
      </div>

      <div className="container-luxury relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div className="px-4 lg:px-8">
            {/* Badge */}
            <div className="mb-8">
              <span className="inline-block px-6 py-2 bg-luxury-gold-500/20 border border-luxury-gold-400 text-luxury-gold-400 text-sm uppercase tracking-wider font-luxury-display font-medium">
                Exclusive Membership
              </span>
            </div>
            
            {/* Main Heading */}
            <h1 className="luxury-heading text-4xl md:text-5xl lg:text-6xl mb-6 text-white animate-luxury-fade-in">
              Join the Elite
            </h1>
            
            {/* Subheading */}
            <h2 className="luxury-subheading text-xl md:text-2xl mb-8 text-luxury-gold-400 font-light">
              Apply for Exclusive Access
            </h2>
            
            {/* Description */}
            <p className="luxury-body text-lg text-luxury-cream-100 mb-12 leading-relaxed">
              Become part of an exclusive community of discerning individuals who appreciate 
              the finest in luxury fashion. Our membership offers unparalleled access to 
              personalized services, exclusive collections, and bespoke experiences.
            </p>
            
            {/* Application Benefits */}
            <div className="bg-luxury-black-900/50 p-6 border border-luxury-gold-500/20 mb-12">
              <h3 className="luxury-subheading text-lg mb-4 text-white">
                Membership Includes
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  "Personal Style Consultation",
                  "Priority Access to Collections", 
                  "Exclusive Member Events",
                  "Complimentary Styling Sessions",
                  "VIP Customer Service",
                  "Global Concierge Support"
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-luxury-gold-500 rounded-full" />
                    <span className="text-luxury-cream-100 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                to="#application-form"
                className="btn-luxury-primary text-center min-w-[200px]"
              >
                Start Application
              </a>
              
              <Link
                to="/member-services"
                className="btn-luxury-secondary-dark text-center min-w-[200px]"
              >
                Learn More
              </Link>
            </div>
          </div>

          {/* Membership Tiers Preview */}
          <div className="space-y-6">
            <h3 className="luxury-subheading text-2xl mb-8 text-white">
              Choose Your Tier
            </h3>
            
            {membershipTiers.map((tier, index) => (
              <div 
                key={index}
                className="bg-luxury-black-900/70 p-6 border border-luxury-gold-500/20 hover:border-luxury-gold-500/40 transition-all duration-300 group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="luxury-subheading text-xl text-white group-hover:text-luxury-gold-300 transition-colors duration-300">
                      {tier.name}
                    </h4>
                    <div className="text-luxury-gold-400 font-medium">{tier.price}</div>
                  </div>
                  
                  {tier.highlight && (
                    <div className="bg-luxury-gold-500 text-luxury-black-950 px-3 py-1 text-xs uppercase tracking-wider font-medium">
                      {tier.highlight}
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  {tier.benefits.map((benefit, benefitIndex) => (
                    <div key={benefitIndex} className="flex items-center space-x-2">
                      <span className="text-luxury-gold-400 text-sm">✓</span>
                      <span className="text-luxury-cream-100 text-sm">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            
            <div className="text-center mt-8">
              <p className="text-luxury-cream-200 text-sm mb-4">
                All memberships include our signature welcome package
              </p>
              <Link
                to="/member-services"
                className="text-luxury-gold-400 hover:text-luxury-gold-300 text-sm font-medium uppercase tracking-wider transition-colors duration-300"
              >
                Compare All Benefits →
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 hidden lg:block">
        <div className="flex flex-col items-center text-luxury-cream-200">
          <span className="text-sm uppercase tracking-wider font-luxury-display mb-4">
            Application Process
          </span>
          <div className="w-px h-16 bg-gradient-to-b from-luxury-gold-500 to-transparent animate-pulse" />
        </div>
      </div>
    </section>
  )
}

export default MembershipApplicationHero